import type { PublicationModel } from "../../types/models";
import { isMovableAutoPost } from "./engine/serverSync";
import type { SchedulingBatchRequest } from "./schedulingBatch";

export const RECOMMENDED_POST_SPACING_MS = 60 * 60 * 1000;
export type PublicationSpacingPost = Pick<PublicationModel, "id" | "projectId" | "provider" | "accountId" | "scheduledAt" | "status">
  & Partial<Pick<PublicationModel, "autoFolderId">>;
const spacingAccountKey = (provider: string, accountId: string) => JSON.stringify([provider, accountId]);
const occupiesTime = (status: string) => ["scheduled", "publishing", "verifying", "published"].includes(status);

/** Review the resulting batch, including other projects and both sides of each
 * changed time. Caption-only edits and unrelated existing clusters do not warn.
 * Epoch differences make the advice independent of clock-hour and DST boundaries. */
export function findCloselySpacedPublications(
  request: SchedulingBatchRequest,
  publications: readonly PublicationSpacingPost[],
): PublicationSpacingPost[] {
  const finalPosts = new Map(publications.map(post => [post.id, post]));
  const changedIds = new Set<string>();
  const baseline = new Map(request.expected_publications.map(post => [post.id, post]));
  for (const id of request.delete_ids) finalPosts.delete(id);
  for (const update of request.updates) {
    const previous = baseline.get(update.id);
    if (!previous) continue;
    // The server keeps Auto post ownership unless the time or status changes.
    const keepsOwnership = previous.status === update.status &&
      new Date(previous.scheduled_at).getTime() === new Date(update.scheduled_at).getTime();
    finalPosts.set(update.id, { id: update.id, projectId: previous.project_id, accountId: previous.account_id, provider: previous.provider,
      scheduledAt: new Date(update.scheduled_at), status: update.status,
      autoFolderId: keepsOwnership ? finalPosts.get(update.id)?.autoFolderId ?? null : null });
    if (update.status === "scheduled" && (previous.status !== "scheduled" ||
      new Date(previous.scheduled_at).getTime() !== new Date(update.scheduled_at).getTime())) changedIds.add(update.id);
  }
  for (const post of request.creates) {
    finalPosts.set(post.id, { id: post.id, projectId: post.project_id, accountId: post.account_id, provider: post.provider,
      scheduledAt: new Date(post.scheduled_at), status: "scheduled" });
    changedIds.add(post.id);
  }
  const byAccount = new Map<string, Array<PublicationSpacingPost & { time: number }>>();
  for (const post of finalPosts.values()) {
    const time = new Date(post.scheduledAt).getTime();
    // An Auto post the planner still moves makes way for the user's: no advice on it.
    if (!occupiesTime(post.status) || !Number.isFinite(time) || isMovableAutoPost({ autoFolderId: post.autoFolderId ?? null, ...post })) continue;
    const key = spacingAccountKey(post.provider, post.accountId);
    const group = byAccount.get(key) ?? [];
    group.push({ ...post, time });
    byAccount.set(key, group);
  }
  const affectedPosts: PublicationSpacingPost[] = [];
  for (const posts of byAccount.values()) {
    posts.sort((a, b) => a.time - b.time || a.id.localeCompare(b.id));
    const affected = new Set<string>();
    for (let i = 0; i < posts.length; i++) {
      for (let j = i + 1; j < posts.length; j++) {
        const earlier = posts[i], later = posts[j];
        const gapMs = later.time - earlier.time;
        if (gapMs >= RECOMMENDED_POST_SPACING_MS) break;
        if (!changedIds.has(earlier.id) && !changedIds.has(later.id)) continue;
        affected.add(earlier.id);
        affected.add(later.id);
      }
    }
    affectedPosts.push(...posts.filter(post => affected.has(post.id)).map(post => finalPosts.get(post.id)!));
  }
  return affectedPosts;
}
